import { useEffect, useState } from 'preact/hooks';
import { useTranslation } from '../i18n';
import { useRelativeTime } from '../i18n/useRelativeTime';
import { SectionHeading, ArrowLink, Skeleton, PageBody } from '../primitives';
import { coverUrl } from '../api/articles';

/**
 * Landing-page editorial slot. Fetches the four newest articles from /api/articles?size=4
 * (server-side default sort is `-PublishedAt`) and renders the top one as a hero next to a
 * vertical stack of three smaller follow-up cards at lg+. Below lg the layout collapses to a
 * single column.
 *
 * The "1 main + 3 side" layout mirrors the legacy www landing's editorial slot. Cover images
 * route through the Koan.Media recipe pipeline at /media/{coverMediaId}/{recipe}; the
 * `article-hero` recipe pins the format/quality and accepts dimension overrides for the
 * landing slot's wider-aspect (900x675) variant.
 *
 * Hide the section entirely when there's nothing to feature or the fetch errors - better than
 * an empty card or a tombstone tile.
 */

// Slim Article shape - only the fields rendered below. The API returns the full entity; we
// narrow here so the bundle doesn't carry types it never uses.
interface FeaturedArticle {
    id: string;
    slug: string;
    title?: string | null;
    subtitle?: string | null;
    excerpt?: string | null;
    renderedExcerpt?: string | null;
    readingMinutes: number;
    publishedAt?: string | null;
    displayDate?: string | null;
    coverMediaId?: string | null;
    tags?: {
        public?: { flat?: string[] | null } | null;
        publicTags?: string[] | null;
    } | null;
}

type Fetched =
    | { status: 'loading' }
    | { status: 'success'; data: FeaturedArticle[] }
    | { status: 'error' };

export function LatestArticle({ articlesHref = '/articles' }: { articlesHref?: string } = {}) {
    const t = useTranslation();
    const [state, setState] = useState<Fetched>({ status: 'loading' });

    useEffect(() => {
        let cancelled = false;
        fetch('/api/articles?size=4', { credentials: 'omit' })
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json() as Promise<FeaturedArticle[]>;
            })
            .then((rows) => {
                if (!cancelled) setState({ status: 'success', data: rows });
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'error' });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (state.status === 'success' && state.data.length === 0) return null;
    if (state.status === 'error') return null;

    // Id-routed with a decorative trailing slug (E3): the id resolves, the
    // slug is cosmetic and omitted when absent.
    const buildHref = (article: FeaturedArticle) =>
        article.slug && article.slug.length > 0
            ? `/articles/${encodeURIComponent(article.id)}/${encodeURIComponent(article.slug)}`
            : `/articles/${encodeURIComponent(article.id)}`;

    return (
        <section class="border-b border-rule">
            <PageBody rhythm="section">
                <div class="mb-8 flex flex-wrap items-end justify-between gap-4">
                    <SectionHeading
                        eyebrow={t('landing.latestArticleEyebrow')}
                        title={t('landing.latestArticleTitle')}
                        size="md"
                        as="h2"
                    />
                    <ArrowLink href={articlesHref} variant="primary">
                        {t('landing.latestArticleAll').replace(/\s*→\s*$/, '')}
                    </ArrowLink>
                </div>

                {state.status === 'loading' && <LoadingSkeleton />}

                {state.status === 'success' && state.data.length > 0 && (
                    <ArticleShowcase
                        hero={state.data[0]}
                        rest={state.data.slice(1, 4)}
                        buildHref={buildHref}
                    />
                )}
            </PageBody>
        </section>
    );
}

function LoadingSkeleton() {
    return (
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr]">
            <Skeleton.Line height="100%" class="aspect-[4/3] rounded-md" />
            <div class="flex flex-col gap-4">
                {[0, 1, 2].map((i) => (
                    <div key={i} class="flex gap-4">
                        <Skeleton.Line width="120px" height="80px" class="rounded-md" />
                        <div class="flex flex-1 flex-col gap-2">
                            <Skeleton.Line width="80%" />
                            <Skeleton.Line width="40%" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface ShowcaseProps {
    hero: FeaturedArticle;
    rest: FeaturedArticle[];
    buildHref: (article: FeaturedArticle) => string;
}

function ArticleShowcase({ hero, rest, buildHref }: ShowcaseProps) {
    return (
        // items-start (vs items-stretch) lets the right column sit at its natural height.
        // Otherwise the flex column tries to match the hero's 4:3 cover height, forcing each
        // sub-card huge.
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-start">
            <HeroArticleCard article={hero} buildHref={buildHref} />
            {rest.length > 0 && (
                <div class="flex flex-col gap-4">
                    {rest.map((article) => (
                        <SubArticleCard key={article.id} article={article} buildHref={buildHref} />
                    ))}
                </div>
            )}
        </div>
    );
}

interface CardProps {
    article: FeaturedArticle;
    buildHref: (article: FeaturedArticle) => string;
}

function HeroArticleCard({ article, buildHref }: CardProps) {
    const t = useTranslation();
    const rel = useRelativeTime();
    // Landing hero uses the dedicated article-hero-landing recipe (900x675);
    // the broader article-hero recipe (2000x800) is for the in-page detail header.
    const cover = coverUrl(article, 'article-hero-landing');
    const tags = article.tags?.publicTags ?? article.tags?.public?.flat ?? [];
    const href = buildHref(article);
    const when = article.displayDate ?? article.publishedAt;

    return (
        <a
            href={href}
            class="group relative flex flex-col overflow-hidden rounded-md border border-rule bg-surface-raised transition hover:-translate-y-0.5 hover:border-accent-soft"
        >
            <div class="relative aspect-[4/3] overflow-hidden bg-surface-2">
                {cover ? (
                    <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                ) : (
                    <div class="grid h-full w-full place-items-center bg-gradient-to-br from-surface-2 to-surface-3">
                        <span class="font-mono text-xs text-fg-muted">
                            {t('articles.kindArticle') || 'Article'}
                        </span>
                    </div>
                )}
                {/* Scrim under the bottom of the cover for title legibility. */}
                <div class="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
                <div class="absolute inset-x-0 bottom-0 p-5">
                    <div class="mb-2 flex items-center gap-2">
                        <span class="inline-flex items-center gap-1.5 rounded-full border border-accent-soft bg-accent-glow px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent">
                            <span class="text-accent">✦</span>
                            {t('landing.latestArticlePill')}
                        </span>
                        {tags[0] && (
                            <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                                · {tags[0]}
                            </span>
                        )}
                    </div>
                    <h3 class="font-display text-[clamp(28px,2.6vw,38px)] italic font-normal leading-[1.05] text-fg-primary drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] transition-colors group-hover:text-accent">
                        {article.title ?? t('articles.untitled')}
                    </h3>
                </div>
            </div>

            {(article.subtitle || article.renderedExcerpt || article.excerpt) && (
                <div class="flex flex-col gap-3 p-5">
                    <p class="font-display text-[15px] italic text-fg-muted line-clamp-3">
                        {article.subtitle ?? article.renderedExcerpt ?? article.excerpt}
                    </p>
                    <div class="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                        {when && <span>{rel(when)}</span>}
                        {when && <span>·</span>}
                        <span>{t('articles.readingMinutes', { n: article.readingMinutes || 1 })}</span>
                    </div>
                </div>
            )}
        </a>
    );
}

function SubArticleCard({ article, buildHref }: CardProps) {
    const t = useTranslation();
    const rel = useRelativeTime();
    // Sidebar cards use the dedicated article-card-sidebar recipe (400x400);
    // the broader article-card recipe (480x480) is for the Articles index grid.
    const cover = coverUrl(article, 'article-card-sidebar');
    const href = buildHref(article);
    const when = article.displayDate ?? article.publishedAt;
    const tags = article.tags?.publicTags ?? article.tags?.public?.flat ?? [];

    return (
        <a
            href={href}
            class="group flex flex-1 gap-4 overflow-hidden rounded-md border border-rule bg-surface-raised p-3 transition hover:-translate-y-0.5 hover:border-accent-soft"
        >
            <div class="relative aspect-square w-[180px] flex-none overflow-hidden rounded-sm bg-surface-2">
                {cover ? (
                    <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                ) : (
                    <div class="grid h-full w-full place-items-center bg-gradient-to-br from-surface-2 to-surface-3">
                        <span class="font-mono text-[9px] text-fg-muted">
                            {tags[0] ?? t('articles.kindArticle') ?? 'Article'}
                        </span>
                    </div>
                )}
            </div>

            <div class="flex min-w-0 flex-1 flex-col gap-1.5">
                <h4 class="line-clamp-2 font-display text-[18px] italic font-normal leading-[1.15] text-fg-primary transition-colors group-hover:text-accent">
                    {article.title ?? t('articles.untitled') ?? 'Untitled'}
                </h4>
                {tags[0] && (
                    <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                        {tags[0]}
                    </span>
                )}
                <div class="mt-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                    {when && <span>{rel(when)}</span>}
                    {when && <span>·</span>}
                    <span>{t('articles.readingMinutes', { n: article.readingMinutes || 1 })}</span>
                </div>
            </div>
        </a>
    );
}
